import React, { useEffect, useRef, useState } from 'react';

// Popover that hosts the three per-brief settings (auto-archive,
// supplier notifications mute, 24h auto-reminder) anchored to a cog
// button in the BriefDetail action row. Replaces the previous
// full-width "Brief Settings" collapsible band as part of the brief
// header v2 condense — same controls, ~50px of vertical page space
// reclaimed.
//
// Closes on outside click + Esc; the parent owns the open state so the
// cog button can drive `aria-expanded`. Focus the first toggle on open
// so keyboard users land usefully.
//
// Active-settings dot rule (computed by the parent and passed in via
// `hasActiveSetting`): only NON-DEFAULT settings count. Defaults are
// auto-archive ON, mute OFF, auto-reminder OFF — so the dot appears
// when auto-archive is off, mute is on, or auto-reminder is on.

// Small pill-style toggle to match the spec (30×18, green when on).
// forwardRef so the popover can autofocus the first toggle on open
// (keyboard users land somewhere useful instead of nowhere).
const Toggle = React.forwardRef(function Toggle({ checked, onChange, disabled, ariaLabel }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className="relative inline-flex items-center flex-shrink-0 transition-colors rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-active-green disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        width: 30,
        height: 18,
        background: checked ? '#92D400' : '#D4D9C6',
        border: checked ? '1px solid #6a9a00' : '1px solid #B9C0A9',
      }}
    >
      <span
        className="absolute rounded-full transition-transform"
        style={{
          width: 12,
          height: 12,
          top: 2,
          left: 2,
          // Off knob is WHITE (not near-track grey) so it's clearly visible
          // against the grey track — otherwise the off state reads as a flat
          // pill rather than a switch with the knob to the left.
          background: checked ? '#131313' : '#FFFFFF',
          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          transform: checked ? 'translateX(12px)' : 'translateX(0)',
        }}
      />
    </button>
  );
});

export default function BriefSettingsPopover({
  open,
  onClose,
  // Current settings state (from the brief row)
  autoArchiveEnabled,
  autoArchiveWeeks,
  autoArchiveDefaultWeeks,
  muted,
  autoReminderEnabled,
  // Auto-reminder is guarded by the same 24h-too-late rule the AddBrief
  // / AmendBriefPanel checkboxes enforce. The parent computes whether
  // the deadline is too close and passes it through so the popover
  // doesn't have to reason about it.
  autoReminderDisabled,
  autoReminderDisabledReason,
  // Toggle handlers
  onToggleAutoArchive,
  onToggleMute,
  onToggleAutoReminder,
  // Per-brief weeks override. Pass a number (1–52) to set, or null to
  // clear the override and fall back to the system default. Optional —
  // when omitted, the meta line is read-only.
  onUpdateAutoArchiveWeeks,
}) {
  const wrapperRef = useRef(null);
  const firstToggleRef = useRef(null);
  // Inline weeks edit state. Opens to the current effective value;
  // commit on blur / Enter. Clamps to [1, 52]; empty/invalid input is
  // treated as "no change". Distinct from the toggle so the cog/popover
  // stays unanchored while typing.
  const [editingWeeks, setEditingWeeks] = useState(false);
  const [weeksDraft, setWeeksDraft] = useState('');
  const weeksInputRef = useRef(null);

  // Local weeks state so +/- click feedback is instant — previously the
  // displayed value was driven directly off the prop, which meant the
  // UI couldn't update until the parent's async DB write completed and
  // re-rendered. If the parent's commit silently failed (RLS, network,
  // missing column) the buttons looked broken. Local state shows the
  // new value the moment the click registers; the prop syncs back via
  // the effect below so the popover stays canonical with the parent.
  const [weeksLocal, setWeeksLocal] = useState(autoArchiveWeeks);
  useEffect(() => { setWeeksLocal(autoArchiveWeeks); }, [autoArchiveWeeks]);

  // Close on outside click + Esc. The cog button itself is intentionally
  // OUTSIDE this wrapper (it's the anchor, not the content) so a click
  // on the cog while open routes through its own toggle handler instead
  // of being swallowed by this outside-click logic.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        // Don't close if the click was on the cog itself — the cog
        // already toggles state, and treating it as "outside" would
        // make the popover close-then-reopen on the same click.
        const cogBtn = document.querySelector('[data-brief-settings-cog]');
        if (cogBtn && cogBtn.contains(e.target)) return;
        onClose();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => firstToggleRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const weeksLabel = `${autoArchiveWeeks} ${autoArchiveWeeks === 1 ? 'week' : 'weeks'}`;
  const canEditWeeks = typeof onUpdateAutoArchiveWeeks === 'function';
  const defaultWeeks = Number.isFinite(Number(autoArchiveDefaultWeeks)) && Number(autoArchiveDefaultWeeks) > 0
    ? Number(autoArchiveDefaultWeeks)
    : 4;
  const isOverridden = Number(autoArchiveWeeks) !== defaultWeeks;

  const beginEditWeeks = () => {
    if (!canEditWeeks || !autoArchiveEnabled) return;
    setWeeksDraft(String(autoArchiveWeeks));
    setEditingWeeks(true);
    requestAnimationFrame(() => {
      weeksInputRef.current?.focus();
      weeksInputRef.current?.select();
    });
  };
  const commitWeeks = () => {
    const raw = String(weeksDraft).trim();
    setEditingWeeks(false);
    if (raw === '') return;
    const n = Math.max(1, Math.min(52, parseInt(raw, 10)));
    if (!Number.isFinite(n) || n === Number(autoArchiveWeeks)) return;
    onUpdateAutoArchiveWeeks(n);
  };
  const resetWeeks = () => {
    if (!canEditWeeks) return;
    setEditingWeeks(false);
    onUpdateAutoArchiveWeeks(null);
  };

  return (
    <div
      ref={wrapperRef}
      role="dialog"
      aria-label="Brief settings"
      className="absolute z-50 rounded-[10px] shadow-lift"
      style={{
        top: 'calc(100% + 10px)',
        right: 0,
        width: 320,
        background: '#FFFFFF',
        border: '1px solid #E6EADB',
      }}
    >
      {/* Arrow pointing up to the cog — drawn as two divs so the
          border line goes around the arrow body without needing SVG. */}
      <div
        aria-hidden="true"
        className="absolute"
        style={{
          top: -7,
          right: 12,
          width: 12,
          height: 12,
          background: '#FFFFFF',
          borderTop: '1px solid #E6EADB',
          borderLeft: '1px solid #E6EADB',
          transform: 'rotate(45deg)',
        }}
      />

      {/* Title row */}
      <div className="px-4 py-3 border-b border-line">
        <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">
          Brief Settings
        </p>
      </div>

      {/* Auto-archive */}
      <div className="flex items-start gap-3 px-4 py-3" style={{ borderBottom: '1px solid #E6E7E1' }}>
        <div className="flex-1 min-w-0">
          <p className="font-['Metropolis'] text-[12px] font-bold text-ink">Auto-archive</p>
          {/* Custom stepper — replaces the native <input type="number">
              whose browser spinners looked off-brand and tiny. Minus and
              plus pills flank the value with the number editable in-line
              (click selects all, type to replace, Enter commits). Keeps
              keyboard accessibility while looking like part of the popover
              chrome rather than a bare form input.

              When not editing, an "Edit" affordance shows up on hover as
              a tiny pencil so the row stays calm by default. */}
          {canEditWeeks && autoArchiveEnabled ? (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">After</span>
              <div className="inline-flex items-center bg-white border border-line rounded-md overflow-hidden" style={{ height: 24 }}>
                <button
                  type="button"
                  onClick={() => {
                    const cur = editingWeeks ? (parseInt(weeksDraft, 10) || weeksLocal) : weeksLocal;
                    const next = Math.max(1, cur - 1);
                    if (next === weeksLocal) return;
                    setWeeksLocal(next);
                    if (editingWeeks) setWeeksDraft(String(next));
                    onUpdateAutoArchiveWeeks(next);
                  }}
                  disabled={weeksLocal <= 1}
                  aria-label="Decrease weeks"
                  className="w-6 h-full inline-flex items-center justify-center text-fusion-mid-grey hover:text-ink hover:bg-active-green/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <div className="border-x border-line h-full inline-flex items-center justify-center" style={{ minWidth: 28 }}>
                  {editingWeeks ? (
                    <input
                      ref={weeksInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={weeksDraft}
                      onChange={e => setWeeksDraft(e.target.value.replace(/[^0-9]/g, ''))}
                      onBlur={commitWeeks}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitWeeks(); }
                        if (e.key === 'Escape') { e.preventDefault(); setEditingWeeks(false); }
                      }}
                      className="w-7 bg-transparent text-ink font-['Metropolis'] text-[12px] font-bold text-center focus:outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={beginEditWeeks}
                      title="Click to edit"
                      className="text-ink font-['Metropolis'] text-[12px] font-bold hover:text-active-green focus:outline-none focus:text-fusion-forest transition-colors px-1.5"
                    >
                      {weeksLocal}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const cur = editingWeeks ? (parseInt(weeksDraft, 10) || weeksLocal) : weeksLocal;
                    const next = Math.min(52, cur + 1);
                    if (next === weeksLocal) return;
                    setWeeksLocal(next);
                    if (editingWeeks) setWeeksDraft(String(next));
                    onUpdateAutoArchiveWeeks(next);
                  }}
                  disabled={weeksLocal >= 52}
                  aria-label="Increase weeks"
                  className="w-6 h-full inline-flex items-center justify-center text-fusion-mid-grey hover:text-ink hover:bg-active-green/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
              <span className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">
                {weeksLocal === 1 ? 'week' : 'weeks'} of completion
              </span>
              {isOverridden && (
                <button
                  type="button"
                  onClick={resetWeeks}
                  className="font-['Metropolis'] text-[10px] font-bold tracking-[0.1em] uppercase text-fusion-forest hover:text-fusion-forest focus:outline-none"
                  title={`Reset to system default (${defaultWeeks} ${defaultWeeks === 1 ? 'week' : 'weeks'})`}
                >
                  Reset
                </button>
              )}
            </div>
          ) : (
            <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey mt-0.5">
              After {weeksLabel} of completion
            </p>
          )}
        </div>
        <Toggle
          ref={firstToggleRef}
          checked={autoArchiveEnabled}
          onChange={onToggleAutoArchive}
          ariaLabel="Toggle auto-archive"
        />
      </div>

      {/* Mute notifications */}
      <div className="flex items-start gap-3 px-4 py-3" style={{ borderBottom: '1px solid #E6E7E1' }}>
        <div className="flex-1 min-w-0">
          <p className="font-['Metropolis'] text-[12px] font-bold text-ink">Mute notifications</p>
          <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey mt-0.5">
            Pause supplier emails about this brief
          </p>
        </div>
        <Toggle
          checked={muted}
          onChange={onToggleMute}
          ariaLabel={muted ? 'Unmute supplier notifications' : 'Mute supplier notifications'}
        />
      </div>

      {/* Auto-reminder */}
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p className="font-['Metropolis'] text-[12px] font-bold text-ink">Auto-reminder</p>
          <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey mt-0.5">
            24h before deadline
          </p>
          {autoReminderDisabled && autoReminderDisabledReason && (
            <p className="font-['Metropolis'] text-[10px] text-[#7A4E10] mt-1 leading-snug">
              {autoReminderDisabledReason}
            </p>
          )}
        </div>
        <Toggle
          checked={autoReminderEnabled && !autoReminderDisabled}
          onChange={onToggleAutoReminder}
          disabled={autoReminderDisabled}
          ariaLabel="Toggle auto-reminder"
        />
      </div>
    </div>
  );
}
